import loginScreenImageUrl from "@/assets/login-screen.svg"
import { Button } from "@/components/chadcn/Button"
import { Label } from "@/components/chadcn/Label"
import CopyRightsFooter from "@/components/CopyRightsFooter"
import HeroImage from "@/components/HeroImage"
import { EmailInput, PasswordInput } from "@/components/InputFields"
import Link from "@/components/Link"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useLogInMutation } from "@/data/queries"
import { useAuthTokensAtom } from "@/data/store"
import { backendErrorCodeFor } from "@/model/BackendErrors"
import { ROUTES } from "@/routes"
import { signInSchema, type SignInFormData } from "@/schemas/auth"
import PostLoginRedirect from "@/screens/auth/PostLoginRedirect"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, useWatch } from "react-hook-form"
import { generatePath, useLocation, useSearchParams } from "react-router"

const version = import.meta.env.VITE_APP_VERSION as string | undefined

type SignInPageStateParams = {
  email?: string
}

const SignInPage = () => {
  const [authTokens] = useAuthTokensAtom()
  const [searchParams] = useSearchParams()
  const locationState = useLocation().state as SignInPageStateParams | null
  // Device-registration SMS links point existing patients at the sign-in
  // page with ?d=<deviceCode>; linking the device wins over ?redirect=.
  const deviceCode = searchParams.get("d")
  const redirect = deviceCode
    ? generatePath(ROUTES.ADD_DEVICE, { deviceCode })
    : searchParams.get("redirect")
  const { mutateAsync: logIn, isPending, error: logInError } = useLogInMutation()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SignInFormData>({
    mode: "all",
    defaultValues: { email: locationState?.email ?? "", password: "" },
    resolver: zodResolver(signInSchema),
  })
  // useWatch (not getValues): the Link's state prop is captured at render
  // time, so the form must re-render on every keystroke for the link to carry
  // the latest value.
  const emailValue = useWatch({ control, name: "email" })

  if (authTokens !== null) {
    return <PostLoginRedirect redirect={redirect} />
  }

  const logInErrorCode = backendErrorCodeFor(logInError)

  const errorMessage =
    logInErrorCode === "LOGIN_FAILED"
      ? "Email or password is incorrect."
      : logInErrorCode === "USER_DISABLED"
        ? "Your account is disabled. Please contact support."
        : logInErrorCode
          ? "Something went wrong. Please try again."
          : null

  const onSubmit = async (formData: SignInFormData) => {
    try {
      await logIn(formData)
    } catch {
      // captured by useMutation; surfaced via logInError
    }
  }

  return (
    <Page>
      <HeroImage src={loginScreenImageUrl} alt="login" />
      <h2 className="mb-8 px-6 text-center text-h2 font-bold">
        Please enter your email and password to login
      </h2>

      <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-1">
          <Label htmlFor="email">Email</Label>
          <EmailInput
            id="email"
            autoComplete="username"
            disabled={isPending}
            {...register("email")}
            placeholder="email@example.com"
            isError={!!errors.email?.message}
          />
          {errors.email?.message && <p className="text-sm text-error">{errors.email.message}</p>}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            disabled={isPending}
            {...register("password")}
            placeholder="password"
            isError={!!errors.password?.message}
          />
          {errors.password?.message && (
            <p className="text-sm text-error">{errors.password.message}</p>
          )}
        </div>

        <div className="flex justify-end text-sm">
          <Link to={ROUTES.FORGOT_PASSWORD} state={{ email: emailValue }}>
            Forgot Password?
          </Link>
        </div>

        {errorMessage && (
          <div className="rounded-md border border-error/30 bg-error/5 p-3">
            <p className="text-sm text-error">{errorMessage}</p>
          </div>
        )}

        <Button type="submit" disabled={isPending} className="mt-8 w-full">
          {isPending && <Spinner />}
          Log in
        </Button>
      </form>

      <CopyRightsFooter />
      {version && <p className="mt-2 text-right text-xs text-primary-contrast/60">v{version}</p>}
    </Page>
  )
}

export default SignInPage
